import { motion } from "framer-motion";

const ABOUT_IMG = "/about-esd-valve.jpg";

const CAPABILITIES = [
    "API 6A & API 6D manufacturing scope",
    "In-house hydrostatic & pneumatic testing",
    "NACE MR0175 sour-service trim options",
    "Fire-safe, fugitive-emission & SIL configurations",
];

const fadeUp = {
    initial: { opacity: 0, y: 28 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.15 },
};

export default function About() {
    return (
        <section
            id="about"
            data-testid="about-section"
            className="tech-grid relative"
        >
            <div className="mx-auto grid max-w-7xl gap-14 px-6 py-24 sm:px-8 lg:grid-cols-2 lg:gap-20 lg:py-32">
                <div>
                    <motion.p
                        {...fadeUp}
                        transition={{ duration: 0.6 }}
                        className="mb-5 flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-tactical"
                    >
                        <span className="h-px w-10 bg-tactical" />
                        The Manufacturer
                    </motion.p>
                    <motion.h2
                        {...fadeUp}
                        transition={{ duration: 0.7, delay: 0.08 }}
                        className="font-display text-5xl font-bold uppercase leading-[0.95] tracking-tight text-slate-100 sm:text-6xl"
                    >
                        Built in Britain.
                        <br />
                        <span className="text-tactical">Proven in the field.</span>
                    </motion.h2>
                    <motion.p
                        {...fadeUp}
                        transition={{ duration: 0.7, delay: 0.16 }}
                        className="mt-7 max-w-xl leading-relaxed text-slate-400"
                    >
                        Blackhawk Valves is a UK-based manufacturer of
                        critical-service valves for the oil and gas industry.
                        From API 6A wellhead equipment to API 6D pipeline
                        systems, every valve is engineered, machined, assembled
                        and pressure-tested before it leaves our factory.
                    </motion.p>
                    <motion.p
                        {...fadeUp}
                        transition={{ duration: 0.7, delay: 0.22 }}
                        className="mt-4 max-w-xl leading-relaxed text-slate-400"
                    >
                        Our engineers work directly with procurement teams and
                        EPC contractors to tailor materials, trim and actuation
                        to the duty — sour gas, cryogenics, extreme pressure,
                        fire-safe.
                    </motion.p>
                    <motion.ul
                        {...fadeUp}
                        transition={{ duration: 0.7, delay: 0.3 }}
                        className="mt-9 space-y-0 border-t border-white/10"
                    >
                        {CAPABILITIES.map((c) => (
                            <li
                                key={c}
                                className="flex items-center gap-4 border-b border-white/10 py-4 font-mono text-xs uppercase tracking-[0.14em] text-slate-300"
                            >
                                <span className="h-1.5 w-1.5 rotate-45 bg-tactical" />
                                {c}
                            </li>
                        ))}
                    </motion.ul>
                </div>

                <motion.div
                    {...fadeUp}
                    transition={{ duration: 0.8, delay: 0.15 }}
                    className="relative"
                >
                    <div className="relative overflow-hidden">
                        <img
                            src={ABOUT_IMG}
                            alt="CAD model of a 150mm ESD actuated ball valve engineered by Blackhawk"
                            className="h-full min-h-[420px] w-full object-cover grayscale-[30%]"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 via-transparent to-transparent" />
                        {/* corner brackets */}
                        <span className="absolute left-3 top-3 h-5 w-5 border-l-2 border-t-2 border-tactical" />
                        <span className="absolute right-3 top-3 h-5 w-5 border-r-2 border-t-2 border-tactical" />
                        <span className="absolute bottom-3 left-3 h-5 w-5 border-b-2 border-l-2 border-tactical" />
                        <span className="absolute bottom-3 right-3 h-5 w-5 border-b-2 border-r-2 border-tactical" />
                    </div>
                    <div
                        data-testid="british-heritage-badge"
                        className="absolute -bottom-5 left-6 flex items-center gap-3 border border-steel bg-obsidian-800 px-6 py-4 sm:left-10"
                    >
                        <svg viewBox="0 0 24 16" className="h-4 w-6" aria-hidden="true">
                            <path d="M0 0h24v16H0z" fill="#0A0D12" />
                            <path
                                d="M0 0 24 16M24 0 0 16M12 0v16M0 8h24"
                                stroke="#2A3648"
                                strokeWidth="4"
                            />
                            <path
                                d="M0 0 24 16M24 0 0 16M12 0v16M0 8h24"
                                stroke="#F59E0B"
                                strokeWidth="2"
                            />
                        </svg>
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-tactical">
                                United Kingdom
                            </p>
                            <p className="font-display text-lg font-bold uppercase tracking-wide text-slate-100">
                                British Manufacturing
                            </p>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
